
import { computed, onMounted } from "vue";
import {
  defineComponent,
  ref,
  reactive,
  watch,
  onBeforeUnmount,
  PropType
} from "vue";
import BreakdownTableLoading from "./breakdown-table-loading.vue";
import BreakdownTableError from "./breakdown-table-error.vue";
import { formatPriceRP } from "@/app/infrastructures/misc/Utils";
import moment from "moment";
import { useI18n } from 'vue-i18n';

interface BreakdownItem {
  name: string;
  total: number;
  percentageChange: number;
  data: Record<string, { value: number; percentageChange: number }>;
}

interface Section {
  title: string;
  data: BreakdownItem[];
}

export default defineComponent({
  name: "BreakdownTableSticky",
  components: {
    BreakdownTableLoading,
    BreakdownTableError
  },
  props: {
    // props menerima contract API yang strukturnya sama seperti simulationData sebelumnya
    dataBreakdown: {
      type: Object as PropType<any>,
      required: true
    },
    dataTypes: {
      default: "omzet",
      type: String as PropType<string>
    },
    loading: {
      default: false,
      type: Boolean as PropType<boolean>
    },
    error: {
      default: false,
      type: Boolean as PropType<boolean>
    },
    groupBy: {
      default: "daily",
      type: String as PropType<string>
    }
  },
  setup(props) {
    const { t } = useI18n();
    const headers = ref<
      {
        raw: string;
        label: string;
        start: Date;
        end: Date;
        type: string;
      }[]
    >([]);

    const tableSections = ref<Section[]>([]);

    // visible counts per section (untuk tombol "lihat lebih banyak")
    const visibleCounts = reactive<Record<string, number>>({});

    // refs untuk sinkronisasi scroll horizontal
    const scrollContainer = ref<HTMLElement | null>(null);
    const scrollContent = ref<HTMLElement | null>(null);
    const sectionScrolls = ref<HTMLElement[]>([]);
    const scrollHandlers = new Map<HTMLElement, (e: Event) => void>();
    let isSyncing = false;
    const chartData = computed(() => (props.dataBreakdown as any)?.chart);
    const lastSyncAt = computed(() =>
      moment((props.dataBreakdown as any)?.lastSyncAt).format(
        "DD MMMM YYYY, HH:mm"
      )
    );
    const groupBy = props.groupBy ?? "daily";
    const isEmpty = computed(() => {
      const breakdown = (props.dataBreakdown as any)?.breakdown ?? {};
      return Object.values(breakdown).every(
        (v) => !Array.isArray(v) || v.length === 0
      );
    });

    const containerWidth = ref(0);
    const isMobile = computed(() => containerWidth.value <= 640);
    const freezeCol1Width = computed(() => (isMobile.value ? 36 : 48));
    const freezeCol2Width = computed(() => (isMobile.value ? 220 : 280));
    const MIN_DATA_COL_WIDTH = computed(() => (isMobile.value ? 120 : 140));
    const dynamicColWidth = computed(() => {
      const count = headers.value.length || 0;
      const fixed = freezeCol1Width.value + freezeCol2Width.value;
      const available = Math.max(0, containerWidth.value - fixed);
      if (count <= 0) return MIN_DATA_COL_WIDTH.value;
      return Math.max(MIN_DATA_COL_WIDTH.value, Math.floor(available / count));
    });

    const updateContainerWidth = () => {
      containerWidth.value = scrollContainer.value?.clientWidth ?? 0;
    };

    onMounted(() => {
      updateContainerWidth();
      window.addEventListener("resize", updateContainerWidth);
    });

    function formatSingleDate(date: Date) {
      return date.toLocaleDateString("id-ID", {
        day: "numeric",
        month: "short"
      });
    }

    function formatRangeLabel(raw: string) {
      const [dates, week] = raw.split(" (");
      const [start, end] = dates.split(" - ");

      const format = (d: string) => {
        const date = new Date(d);
        return `${date.getDate()} ${date.toLocaleString("id-ID", {
          month: "short"
        })}`;
      };

      return `${format(start)} - ${format(end)} (${week}`;
    }

    function parseHeaderKey(key: string) {
      const singleDate = Date.parse(key);
      if (!isNaN(singleDate)) {
        return {
          type: "daily",
          start: new Date(singleDate),
          end: new Date(singleDate),
          label: formatSingleDate(new Date(singleDate))
        };
      }

      // Extract leading range before "("
      const pureRangeText = key.split("(")[0].trim(); // "3 - 6 Apr"
      const [startStr, endStr] = pureRangeText.split("-").map((s) => s.trim());

      const start = new Date(startStr + " 2025"); // Fallback: add year if missing
      const end = new Date(endStr + " 2025");

      return {
        type: key.includes("Week")
          ? "weekly"
          : key.includes("Month")
          ? "monthly"
          : "range",
        start,
        end,
        label: formatRangeLabel(key)
      };
    }

    const formatCurrency = (val: number) => {
      return formatPriceRP(val);
    };

    const formatDate = (date: string) => {
      const d = new Date(date);
      if (isNaN(d.getTime())) return date;
      return d.toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "short"
      });
    };

    const chipsClass = (change: number) =>
      change > 0
        ? "bg-green-lp-1700 text-green-lp-100"
        : change < 0
        ? "bg-red-lp-2500 text-red-lp-100"
        : "bg-white text-gray-lp-300";

    const rowClass = (change?: number) =>
      change == null
        ? ""
        : change > 0
        ? "bg-green-lp-1700"
        : change < 0
        ? "bg-red-lp-2500"
        : "";

    const subHeaderClass = (date: string) => {
      const data = chartData.value.find((item: any) => item.date === date);
      const change = data.percentageChange || 0;
      return change > 0
        ? "text-green-lp-100"
        : change < 0
        ? "text-red-lp-100"
        : "text-black";
    };

    // visibleData fungsi seperti aslinya
    const visibleData = (section: Section) =>
      section.data.slice(0, visibleCounts[section.title] ?? 0);

    const ITEMS_PER_PAGE = 10;

    const loadMore = (sectionTitle: string) => {
      visibleCounts[sectionTitle] =
        (visibleCounts[sectionTitle] || 0) + ITEMS_PER_PAGE;
    };

    function valueSubHeader(date: string) {
      const data = chartData.value.find((item: any) => item.date === date);
      if (props.dataTypes === "berat") return `${data?.totalWeightKg || 0} Kg`;
      if (props.dataTypes === "stt") return `${data?.totalStt || 0} STT`;
      return formatCurrency(data?.totalRevenue || 0);
    }

    function valueBasedOnType(value: number) {
      if (props.dataTypes === "berat") return `${value} Kg`;
      if (props.dataTypes === "stt") return `${value} STT`;
      return formatCurrency(value);
    }

    // Transform props.simulationData ke tableSections + headers
    function mappingDataFromParent(sim: any) {
      // reset
      tableSections.value = [];
      headers.value = [];

      if (!sim) return;

      // jika contract memakai struktur lama (top-level breakdown)
      const breakdown = sim.breakdown ?? sim;

      // possible keys in breakdown - adjust based on contract
      const mapping = [
        { key: "codTypes", title: t("dashboard.breakdownTable.service") },
        { key: "channels", title: t("dashboard.breakdownTable.channel") },
        { key: "productTypes", title: t("dashboard.breakdownTable.shipmentType") },
        { key: "destinationCities", title: t("dashboard.breakdownTable.destinationCity") },
        { key: "commodities", title: t("dashboard.breakdownTable.commodity") }
      ];

      // collect all sections available in breakdown (if a key exists)
      const sections: Section[] = [];

      mapping.forEach((m) => {
        const arr = breakdown?.[m.key];
        if (Array.isArray(arr) && arr.length > 0) {
          const items: BreakdownItem[] = arr.map((it: any) => {
            // ensure item.data has proper shape; if item.data already matches, use it
            const raw = (it.data ?? {}) as Record<
              string,
              { value: number; percentage_change: number }
            >;

            const dataObj: Record<
              string,
              { value: number; percentageChange: number }
            > = Object.fromEntries(
              Object.entries(raw).map(([date, item]) => [
                date,
                {
                  value: item.value,
                  percentageChange: item.percentage_change
                }
              ])
            );

            // calculate total if missing
            const total =
              typeof it.total === "number"
                ? it.total
                : Object.values(dataObj).reduce(
                    (a: number, v: any) => a + (v.value ?? 0),
                    0
                  );

            const percentageChange =
              typeof it.percentageChange === "number" ? it.percentageChange : 0;

            return {
              name: it.name ?? it.title ?? "Unknown",
              total,
              percentageChange,
              data: dataObj
            };
          });

          sections.push({
            title: m.title,
            data: items
          });
        }
      });

      // fallback: if mapping found nothing, try to detect arrays under breakdown and convert them
      if (sections.length === 0 && breakdown && typeof breakdown === "object") {
        Object.keys(breakdown).forEach((k) => {
          const arr = breakdown[k];
          if (Array.isArray(arr) && arr.length > 0 && arr[0].name) {
            const items: BreakdownItem[] = arr.map((it: any) => ({
              name: it.name,
              total: typeof it.total === "number" ? it.total : 0,
              percentageChange:
                typeof it.percentageChange === "number"
                  ? it.percentageChange
                  : 0,
              data: it.data ?? {}
            }));
            sections.push({ title: k, data: items });
          }
        });
      }

      // set tableSections
      tableSections.value = sections;

      // build headers: union of all date keys from all items
      const dateSet = new Set<string>();
      sections.forEach((s) =>
        s.data.forEach((it) => {
          if (it.data && typeof it.data === "object") {
            Object.keys(it.data).forEach((d) => dateSet.add(d));
          }
        })
      );

      // Convert set to sorted array (ascending)
      const dateArr = Array.from(dateSet);
      dateArr.sort((a, b) => {
        const pa = parseHeaderKey(a);
        const pb = parseHeaderKey(b);
        return pa.start.getTime() - pb.start.getTime();
      });

      // if there are many dates, you can slice last N — keep original behavior (30 days) if needed
      headers.value = dateArr.map((key) => {
        const parsed = parseHeaderKey(key);
        return {
          raw: key, // original key from API
          label: parsed.label, // formatted label to display
          start: parsed.start,
          end: parsed.end,
          type: parsed.type
        };
      });

      // initialize visibleCounts per section (default 10)
      Object.assign(visibleCounts, {}); // clear
      sections.forEach((s) => {
        visibleCounts[s.title] = Math.min(ITEMS_PER_PAGE, s.data.length);
      });
    }

    function registerSectionScroll(el: HTMLElement | null) {
      if (!el) return;
      if (scrollHandlers.has(el)) return;

      const handler = (e: Event) => {
        if (isSyncing) return;
        isSyncing = true;
        const left = (e.target as HTMLElement).scrollLeft;
        sectionScrolls.value.forEach((s) => {
          if (s !== (e.target as HTMLElement)) s.scrollLeft = left;
        });
        requestAnimationFrame(() => {
          isSyncing = false;
        });
      };

      el.addEventListener("scroll", handler, { passive: true });
      sectionScrolls.value.push(el);
      scrollHandlers.set(el, handler);
    }

    function cleanupScrollListeners() {
      scrollHandlers.forEach((handler, el) => {
        el.removeEventListener("scroll", handler as EventListener);
      });
      scrollHandlers.clear();
      sectionScrolls.value = [];
    }

    // watch prop changes
    watch(
      () => props.dataBreakdown,
      (newVal) => {
        cleanupScrollListeners();
        mappingDataFromParent(newVal);
      },
      { immediate: true, deep: true }
    );

    onBeforeUnmount(() => {
      cleanupScrollListeners();
      window.removeEventListener("resize", updateContainerWidth);
    });

    return {
      headers,
      tableSections,
      visibleCounts,
      visibleData,
      loadMore,
      formatCurrency,
      formatDate,
      chipsClass,
      rowClass,
      scrollContainer,
      scrollContent,
      valueSubHeader,
      valueBasedOnType,
      subHeaderClass,
      lastSyncAt,
      isEmpty,
      registerSectionScroll,
      dynamicColWidth,
      freezeCol1Width,
      freezeCol2Width
    };
  }
});
